import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useAuth } from './AuthContext';

interface ChatUnreadContextValue {
  unreadMessageCount: number;
}

const ChatUnreadContext = createContext<ChatUnreadContextValue>({
  unreadMessageCount: 0,
});

export const useChatUnread = () => useContext(ChatUnreadContext);

interface ChatUnreadProviderProps {
  children: React.ReactNode;
}

export const ChatUnreadProvider = ({ children }: ChatUnreadProviderProps) => {
  const { userId, isAuthenticated } = useAuth();
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttempts = useRef(0);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMounted = useRef(true);

  const connect = useCallback(() => {
    if (!userId || !isAuthenticated) return;

    const wsUrl = import.meta.env.VITE_APP_WEBSOCKET as string;
    if (!wsUrl) {
      console.warn(
        '[ChatUnread] WebSocket URL not configured — unread counts unavailable. ' +
          'Set VITE_APP_WEBSOCKET in .env.local or Amplify console.'
      );
      return;
    }

    if (wsRef.current) {
      wsRef.current.onclose = null;
      wsRef.current.onerror = null;
      wsRef.current.close();
      wsRef.current = null;
    }

    const socket = new WebSocket(
      `${wsUrl}?userId=${encodeURIComponent(userId)}&role=customer`
    );
    wsRef.current = socket;

    socket.onopen = () => {
      if (!isMounted.current) return;
      reconnectAttempts.current = 0;
      socket.send(
        JSON.stringify({
          event: 'handshake',
          userId,
          role: 'customer',
        })
      );
    };

    socket.onmessage = (event) => {
      if (!isMounted.current) return;
      try {
        const data = JSON.parse(event.data as string);
        console.log('unread message count', data);
        console.log('unread message count event', event);

        if (data.event === 'unreadCount') {
          setUnreadMessageCount(data.data ?? 0);
        } else if (data.event === 'newMessage') {
          setUnreadMessageCount((prev) => prev + 1);
        } else if (
          data.event === 'messageStatusUpdated' &&
          data.data?.status === 'read'
        ) {
          // When messages are marked read, the backend may send updated counts.
          // Decrement locally for immediate feedback; the next unreadCount event
          // will correct the absolute value if needed.
          const readCount = Array.isArray(data.data?.messageIds)
            ? data.data.messageIds.length
            : 1;
          setUnreadMessageCount((prev) => Math.max(0, prev - readCount));
        } else if (data.event === 'messageRead') {
          // Acknowledgment that messages were read — if messageIds present, decrement
          const readCount = Array.isArray(data.data?.messageIds)
            ? data.data.messageIds.length
            : 1;
          setUnreadMessageCount((prev) => Math.max(0, prev - readCount));
        }
      } catch {
        // ignore malformed frames
      }
    };

    const scheduleReconnect = () => {
      if (!isMounted.current) return;
      const delay = Math.min(1000 * 2 ** reconnectAttempts.current, 30_000);
      reconnectAttempts.current += 1;
      reconnectTimer.current = setTimeout(() => {
        if (isMounted.current) connect();
      }, delay);
    };

    socket.onclose = scheduleReconnect;
    socket.onerror = scheduleReconnect;
  }, [userId, isAuthenticated]);

  useEffect(() => {
    isMounted.current = true;
    connect();

    return () => {
      isMounted.current = false;
      if (reconnectTimer.current !== null) {
        clearTimeout(reconnectTimer.current);
        reconnectTimer.current = null;
      }
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.onerror = null;
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [connect]);

  // FIX: Removed setUnreadMessageCount(0) reset on chat page navigation.
  // The reset destroyed the server-authoritative unread count and left the
  // nav badge stuck at 0 until a page refresh or new-message event.
  // The nav badge now always reflects the latest known count from the
  // WebSocket handshake (unreadCount event) + incremental newMessage events.

  return (
    <ChatUnreadContext.Provider value={{ unreadMessageCount }}>
      {children}
    </ChatUnreadContext.Provider>
  );
};
